import { createSelector } from 'reselect';

import type { PreferenceV2Name, PreferenceV2Value } from 'app-components/flux/preferences-v2/preferencesV2Names';
import {
  PREFERENCES_V2_REDUCER_KEY,
  type PreferenceV2Record,
} from 'app-components/flux/preferences-v2/preferencesV2Types';
import type RootState from 'app-components/utils/redux-store/rootState';

type State = Pick<RootState, typeof PREFERENCES_V2_REDUCER_KEY>;

/** A default only stands in for a missing preference, so passing one removes `undefined`. */
export type PreferenceV2ValueWithDefault<Name extends PreferenceV2Name, Default> = undefined extends Default
  ? PreferenceV2Value<Name> | undefined
  : PreferenceV2Value<Name>;

export const getResourceStore = (state: State) => state[PREFERENCES_V2_REDUCER_KEY];
export const getLoadPending = (state: State) => getResourceStore(state).loadPending;
export const getIsLoading = (state: State) => getResourceStore(state).isLoading;
export const getError = (state: State) => getResourceStore(state).error;
export const getLastSyncDate = (state: State) => getResourceStore(state).lastSyncDate;
// note - this gives a key value pairing based on name
export const getPreferencesV2 = (state: State) => getResourceStore(state).resourcesById;

const selectPreferenceObj = createSelector(
  getPreferencesV2,
  (_state: State, name: PreferenceV2Name) => name,
  (preferencesByName, name) => preferencesByName.get(name),
);

export const getPreferenceObj = <Name extends PreferenceV2Name>(
  state: State,
  name: Name,
): PreferenceV2Record<Name> | undefined =>
  // Records keep the value as unknown; the name's PreferenceV2Value entry is what its writers store.
  selectPreferenceObj(state, name) as PreferenceV2Record<Name> | undefined;

export const getPreferenceV2ValueOrUndefined = <Name extends PreferenceV2Name>(
  state: State,
  name: Name,
): NonNullable<PreferenceV2Value<Name>> | undefined => getPreferenceObj(state, name)?.value ?? undefined;

const selectPreferenceValue = createSelector(
  (state: State, name: PreferenceV2Name) => getPreferenceObj(state, name),
  (_state: State, _name: PreferenceV2Name, defaultValue?: unknown) => defaultValue,
  (preferenceObj, defaultValue) => {
    if (!preferenceObj) {
      return defaultValue;
    }
    return preferenceObj?.value;
  },
);

// NoInfer stops an annotated result from inferring a default that the call never passed.
export const getPreferenceValue = <
  Name extends PreferenceV2Name,
  Default extends PreferenceV2Value<Name> | undefined = undefined,
>(
  state: State,
  name: Name,
  defaultValue?: Default,
): PreferenceV2ValueWithDefault<Name, NoInfer<Default>> =>
  // The value has the name's type, or is the default when the preference is missing.
  selectPreferenceValue(state, name, defaultValue) as PreferenceV2ValueWithDefault<Name, NoInfer<Default>>;
